import {
  ANNUAL_HEATING_THERMS_BY_REGION,
  COOLING_MONTH_WEIGHTS_BY_REGION,
  DAYS_IN_MONTH,
  DEFAULT_HOME_SQFT,
  ELECTRIC_HEAT_SHARE_BY_REGION,
  GAS_USD_PER_THERM,
  HEAT_PUMP_COP,
  HEATING_MONTH_WEIGHTS_BY_REGION,
  OIL_USD_PER_THERM,
  SUBSTATE_SHAPE_BLEND,
} from './assumptions';
import { CLIMATE_REGION_BY_STATE } from './climateRegions';
import { REGION_DEFAULT_USAGE_SHAPE, stateUsageShape } from './stateUsageShapes';
import { EnergyLoads, SimulationContext } from './types';

const ZEROS = Array.from({ length: 12 }, () => 0);

const fossilPricePerTherm = (ctx: SimulationContext): number =>
  ctx.heatingFuel === 'OIL' ? OIL_USD_PER_THERM : GAS_USD_PER_THERM;

// A zip whose climate diverges from its state's default (LA in marine-default
// CA, Tahoe in the Sierra) shouldn't inherit the statewide average curve
// wholesale: blend it halfway toward the override region's own pattern so an
// LA bill peaks with summer cooling while Tahoe leans winter.
const regionAdjustedShape = (ctx: SimulationContext): number[] => {
  const stateShape = stateUsageShape(ctx.stateCode) ?? REGION_DEFAULT_USAGE_SHAPE[ctx.climateRegion];
  if (CLIMATE_REGION_BY_STATE[ctx.stateCode] === ctx.climateRegion) {
    return stateShape;
  }

  const regionShape = REGION_DEFAULT_USAGE_SHAPE[ctx.climateRegion];
  return stateShape.map(
    (fraction, month) => (1 - SUBSTATE_SHAPE_BLEND) * fraction + SUBSTATE_SHAPE_BLEND * regionShape[month]
  );
};

// The EIA state shape averages across all heating fuels, so it understates
// the winter peak of an all-electric home. Blend the state shape toward the
// regional heating curve by the home's modeled electric-heating fraction
// (scaled down by COP for heat pump homes, which heat with ~1/COP the kWh).
const fuelAdjustedShape = (ctx: SimulationContext): number[] => {
  const stateShape = regionAdjustedShape(ctx);
  const heatingShare = ELECTRIC_HEAT_SHARE_BY_REGION[ctx.climateRegion];

  let blend = 0;
  if (ctx.heatingFuel === 'ELECTRIC_RESISTANCE') {
    blend = heatingShare;
  }
  if (ctx.heatingFuel === 'HEAT_PUMP') {
    const heatPumpShare = heatingShare / HEAT_PUMP_COP;
    blend = heatPumpShare / (heatPumpShare + (1 - heatingShare));
  }

  const heatingWeights = HEATING_MONTH_WEIGHTS_BY_REGION[ctx.climateRegion];
  return stateShape.map((fraction, month) => (1 - blend) * fraction + blend * heatingWeights[month]);
};

// Floor/excess decomposition: the mildest month (by daily kWh) defines an
// always-on baseload; everything above it is seasonal HVAC, attributed
// between cooling and heating by the regional demand curves.
const decomposeMonthlyTotals = (
  totalKwh: number[],
  ctx: SimulationContext
): { baseloadKwh: number[]; coolingKwh: number[]; heatingKwh: number[] } => {
  const coolingWeights = COOLING_MONTH_WEIGHTS_BY_REGION[ctx.climateRegion];
  const heatingWeights = HEATING_MONTH_WEIGHTS_BY_REGION[ctx.climateRegion];

  const floorDaily = Math.min(...totalKwh.map((kwh, month) => kwh / DAYS_IN_MONTH[month]));
  const baseloadKwh = DAYS_IN_MONTH.map((days) => floorDaily * days);

  // Clamp guards float noise in the floor month, where excess is exactly zero.
  const excessKwh = totalKwh.map((kwh, month) => Math.max(kwh - baseloadKwh[month], 0));
  const coolingKwh = excessKwh.map(
    (excess, month) => (excess * coolingWeights[month]) / (coolingWeights[month] + heatingWeights[month])
  );
  const heatingKwh = excessKwh.map((excess, month) => Math.max(excess - coolingKwh[month], 0));

  return { baseloadKwh, coolingKwh, heatingKwh };
};

export const disaggregateLoad = (ctx: SimulationContext, monthlyBillUsd: number): EnergyLoads => {
  const annualKwh = (monthlyBillUsd * 12) / ctx.ratePerKwhUsd;

  const shape = fuelAdjustedShape(ctx);
  const totalKwh = shape.map((fraction) => annualKwh * fraction);
  const { baseloadKwh, coolingKwh, heatingKwh } = decomposeMonthlyTotals(totalKwh, ctx);

  // No heating system: fold the derived heating component back into baseload
  // so monthly totals are preserved.
  if (ctx.heatingFuel === 'NONE') {
    heatingKwh.forEach((kwh, month) => {
      baseloadKwh[month] += kwh;
      heatingKwh[month] = 0;
    });
  }

  const sizeFactor = (ctx.homeSqft || DEFAULT_HOME_SQFT) / DEFAULT_HOME_SQFT;
  const annualFossilUsd =
    ctx.heatingFuel === 'GAS' || ctx.heatingFuel === 'OIL'
      ? ANNUAL_HEATING_THERMS_BY_REGION[ctx.climateRegion] * sizeFactor * fossilPricePerTherm(ctx)
      : 0;

  const heatingWeights = HEATING_MONTH_WEIGHTS_BY_REGION[ctx.climateRegion];

  return {
    baseloadKwh,
    batterySavingsUsd: [...ZEROS],
    coolingKwh,
    fossilHeatUsd: heatingWeights.map((weight) => annualFossilUsd * weight),
    heatingKwh,
    solarKwh: [...ZEROS],
  };
};
